import { CRUDMutations } from '@/utils/store-utilities'

const defaultItem = (item) => ({ ...item })

const matchesContext = (item, context) => {
  if (!item) {
    return false
  }
  if (context.url && item.url && item.url === context.url) {
    return true
  }
  if (
    context.notable &&
    item.notable &&
    item.notable.type === context.notable.type &&
    Number(item.notable.id) === Number(context.notable.id)
  ) {
    return true
  }
  return false
}

export const state = () => ({
  items: [],
  context: {
    url: null,
    notable: null,
  },
  drawerOpen: false,
  activeTab: 'page',
  categoryFilter: null,
  searchTerm: '',
  formPrefill: null,
  loading: false,
})

export const mutations = {
  ...CRUDMutations(defaultItem),

  setUrlContext(state, url) {
    state.context = { ...state.context, url }
  },

  setNotableContext(state, notable) {
    state.context = { ...state.context, notable }
  },

  clearNotableContext(state) {
    state.context = { ...state.context, notable: null }
  },

  setDrawerOpen(state, open) {
    state.drawerOpen = open
  },

  setActiveTab(state, tab) {
    state.activeTab = tab
  },

  setCategoryFilter(state, category) {
    state.categoryFilter = category
  },

  setSearchTerm(state, term) {
    state.searchTerm = term
  },

  setFormPrefill(state, prefill) {
    state.formPrefill = prefill
  },

  clearFormPrefill(state) {
    state.formPrefill = null
  },

  setLoading(state, value) {
    state.loading = value
  },
}

export const getters = {
  pageItems(state) {
    const seen = new Set()
    return state.items.filter((item) => {
      if (!matchesContext(item, state.context)) {
        return false
      }
      if (seen.has(item.id)) {
        return false
      }
      seen.add(item.id)
      return true
    })
  },

  count(state, contextGetters) {
    return contextGetters.pageItems.length
  },

  visibleItems(state, contextGetters) {
    const base = state.activeTab === 'page' ? contextGetters.pageItems : state.items
    return base.filter((item) => {
      if (state.categoryFilter && item.category !== state.categoryFilter) {
        return false
      }
      if (state.searchTerm) {
        const needle = state.searchTerm.toLowerCase()
        const haystack = (item.title || '').toLowerCase()
        if (!haystack.includes(needle)) {
          return false
        }
      }
      return true
    })
  },

  dashboardAnnotations(state) {
    return state.items.filter((note) => note.date && note.show_on_dashboard)
  },

  annotationsByDate(state, contextGetters) {
    const map = new Map()
    for (const note of contextGetters.dashboardAnnotations) {
      const bucket = map.get(note.date) || []
      bucket.push(note)
      map.set(note.date, bucket)
    }
    return map
  },
}

export const actions = {
  fetchAll({ commit }) {
    commit('setLoading', true)
    return this.$axios
      .$get('admin/notes')
      .then((response) => {
        commit('load', response.data)
      })
      .finally(() => {
        commit('setLoading', false)
      })
  },

  fetchForContext({ dispatch }) {
    return dispatch('fetchAll')
  },

  createNote({ commit }, payload) {
    return this.$axios.$post('admin/notes', payload).then((response) => {
      commit('add', response.data)
      return response.data
    })
  },

  updateNote({ commit }, item) {
    return this.$axios.$put(`admin/notes/${item.id}`, item).then((response) => {
      commit('update', response.data)
      return response.data
    })
  },

  deleteNote({ commit }, item) {
    return this.$axios.$delete(`admin/notes/${item.id}`).then(() => {
      commit('remove', item)
    })
  },

  openDrawer({ commit }, tab = 'page') {
    commit('setActiveTab', tab)
    commit('setDrawerOpen', true)
  },

  closeDrawer({ commit }) {
    commit('setDrawerOpen', false)
    commit('clearFormPrefill')
  },

  setContext({ commit }, payload) {
    if (payload && Object.prototype.hasOwnProperty.call(payload, 'url')) {
      commit('setUrlContext', payload.url)
    }
    if (payload && Object.prototype.hasOwnProperty.call(payload, 'notable')) {
      commit('setNotableContext', payload.notable)
    }
  },

  clearContext({ commit }) {
    commit('setUrlContext', null)
    commit('clearNotableContext')
  },
}
